<script setup lang="ts">
import { useNavigation } from "../../../composables/useNavigation";
import type { Language, NavItem } from "../../../types/navbar.types";

defineProps<{
  navItems: NavItem[];
  languages: Language[];
  user?: unknown; //TODO: add type
}>();
const { navigationState, toggleSubMenu } = useNavigation();
</script>

<template>
  <header class="page-header">
    <nav class="page-header__container">
      <NuxtLink to="/" tabindex="1">
        <FoundationIconsCitizenmLogo />
      </NuxtLink>

      <div class="navigation">
        <ul class="navigation__list">
          <li v-for="navItem in navItems">
            <LayoutNavBarItem
              v-if="navItem.navigationItems"
              :is-active="
                navigationState.openedNavigationDropDownId === navItem.title
              "
              @click.stop="toggleSubMenu(navItem.title)"
            >
              {{ navItem.title }}</LayoutNavBarItem
            >
            <LayoutNavBarItem
              v-else
              :to="navItem.slug || navItem.externalLinkUrl"
              >{{ navItem.title }}</LayoutNavBarItem
            >
            <LayoutNavigationDropDown
              v-if="navItem.navigationItems"
              :is-open="
                navigationState.openedNavigationDropDownId === navItem.title
              "
              :navigation-items="navItem.navigationItems"
            />
          </li>
        </ul>
      </div>

      <div class="navigation navigation--right">
        <ul class="navigation__list">
          <li class="navigation__item--relative">
            <LayoutNavBarItem
              :is-active="
                navigationState.openedNavigationDropDownId === 'language'
              "
              @click="toggleSubMenu('language')"
            >
              <FoundationIconsGlobe />
            </LayoutNavBarItem>
            <LayoutLanguageDropDown
              v-if="navigationState.openedNavigationDropDownId === 'language'"
              :languages="languages"
            />
          </li>
          <li>
            <LayoutNavBarItem to="/get-booking">my booking</LayoutNavBarItem>
          </li>
          <li class="navigation__item--relative">
            <div class="navigation__divider" />
            <template v-if="user">
              <LayoutNavBarItem
                data-testid="logged-in-menu-button"
                :is-active="
                  navigationState.openedNavigationDropDownId === 'login'
                "
                @click="toggleSubMenu('login')"
              >
                Hi, {{ user.username }}
              </LayoutNavBarItem>
              <LayoutLoggedInDropDown
                data-testid="logged-in-menu"
                v-if="navigationState.openedNavigationDropDownId === 'login'"
              />
            </template>

            <!--            TODO: emit login event?-->
            <LayoutNavBarItem
              v-else
              data-testid="log-in-button"
              @click="console.log('handle login!')"
              >login</LayoutNavBarItem
            >
          </li>
        </ul>
      </div>
      <LayoutNavToggle class="navigation__burger" />
    </nav>
  </header>

  <div v-if="navigationState.mobileDrawerOpen" id="mobile-drawer">
    <LayoutMobileDrawer :nav-items="navItems" :languages />
  </div>
</template>

<style scoped lang="scss">
@use "@citizenm/lobby/assets/styles/utils/mixins" as mixins;

.page-header {
  position: sticky;
  display: flex;
  top: 0;
  width: 100%;
  height: var(--header-navigation-height-mobile, 60px);
  align-items: center;
  box-shadow: var(--shadow, 0px 2px 25px 0px rgba(0, 0, 0, 0.06));
  background-color: var(--primary-white, #fff);
  transition: 0.3s;

  @include mixins.lg {
    height: var(--header-navigation-height, 80px);
  }

  &__logo {
    width: 35px;
    height: 35px;
    img {
      width: 100%;
      height: 100%;
    }
    @include mixins.lg {
      width: 50px;
      height: 50px;
    }
  }

  &__container {
    display: flex;
    max-width: var(--max-width, 1200px);
    width: 100%;
    margin: 0 auto;
    padding: 0 var(--spacing-6, 1.5rem);
  }

  .relative {
    position: relative;
  }
}
.navigation {
  display: none;
  align-items: flex-end;
  gap: var(--spacing-8, 2rem);
  margin-left: var(--spacing-12, 3rem);

  @include mixins.lg {
    display: flex;
  }

  &--right {
    margin-left: auto;
    .nav-bar-item {
      font-weight: normal;
    }
  }

  &__list {
    display: flex;
    gap: var(--spacing-8, 2rem);
    list-style-type: none;
    padding: 0;
    margin: 0;
  }

  &__item {
    &--relative {
      position: relative;
    }
  }

  &__divider {
    position: absolute;
    top: 2rem;
    left: -1rem;
    width: 1px;
    height: 20px;
    background-color: var(--color-light-grey-surface, #f1f1f1);
  }

  &__burger {
    margin-left: auto;
    padding-top: var(--space-12, 12px);
    @include mixins.lg {
      display: none;
    }
  }
}
</style>
